/**
 * Created by john on 7/27/15.
 */

'use strict';

const BaseStore = require('fluxible/addons/BaseStore');
const ProxiedCartCollection = require('../core/nav/cart/collections/ProxiedCartCollection.es');
const CartPortfolioModel = require('bunsen/models/portfolio/PortfolioModel');
const enabler = require('../utils/enabler');
const User = require('../models/User');
const chatUtils = require('dibs-buyer-layout/src/utils/chat').default;
const currencyActionConstants = require('../constants/currencyActionConstants.es');

const MAX_CART_COUNT = 9;

class CartStore extends BaseStore {
    constructor(dispatcher) {
        super(dispatcher);
        this._errorMessage = null;
        /*  This should maybe be moved to an action for server-side rendering since it makes use of User */
        this._cartItems = ProxiedCartCollection.getCartCollection();
        this._recentItems = [];
        this._portfolioModel = new CartPortfolioModel();
        this._currency = 'USD';
        this._isDisplayingNotification = false;

        /*
         Listen to any events on the backbone model. This could potentially cause
         a memory leak if these events never get unregistered, so might need to revisit this

         TODO:  make this an action that registers this event handler
         this will cause a memory leak if this store is used for server-side rendering
         */
        this._cartItems.on('reset', () => this._onCartUpdated());
        this._cartItems.on('sync', () => this._onCartUpdated());
    }

    isNotificationVisible() {
        return this._isDisplayingNotification;
    }

    /**
     * Returns the URL for the cart. This is a constant value for now.
     * @returns {string}
     */
    getCartUrl() {
        return '/my/cart/';
    }
    /**
     * Returns the URL for checkout of an item.
     * @param {Item} item
     * @returns {string}
     */
    getCheckoutUrl(item) {
        return '/checkout' + item.getPDPURL(false);
    }

    getCurrency() {
        return this._currency;
    }

    /**
     * Returns the ProxiedCartCollection model for the cart.
     * @returns {*}
     */
    getItemsCollection() {
        return this._cartItems;
    }

    getItemAtIndex(index) {
        return this.getItemsCollection().at(index);
    }

    getItemById(id) {
        return this.getItemsCollection().findWhere({ 'item.id': id });
    }

    /**
     * Returns either a list of item models in the cart, or the recently added items,
     * depending on whether the cart is in notification state or not.
     * @returns {*}
     */
    getItemsInMenu() {
        if (this._isDisplayingNotification) {
            return this._recentItems;
        } else {
            /*  Return the models sorted by create date, since in the hover menu
             we want to show them that way but any new item that gets added goes to the end of
             the list, screwing up the order if we already had items loaded from the fetch
             */
            return this._cartItems.models
                .map(m => m) // so we don't sort the original collection
                .sort((a, b) => {
                    const date1 = a.get('createdDate');
                    const date2 = b.get('createdDate');
                    if (date1 > date2) {
                        return -1;
                    } else if (date1 < date2) {
                        return 1;
                    } else {
                        return 0;
                    }
                })
                .map(m => m.get('item'));
        }
    }

    /**
     * Returns a PortfolioModel representing the items in the cart. This gets passed to
     * an ItemsView from CartWrapperView.
     * @returns {PortfolioModel|exports|module.exports|*}
     */
    getPortfolioModel() {
        return this._portfolioModel;
    }

    /**
     * Returns the number of items in the cart.
     * @returns {*}
     */
    getItemCount() {
        return this._cartItems.length;
    }

    /**
     * Returns the number of items in the cart, or "<Max_Cart_Count>" if it's greater than that value
     * @returns {*}
     */
    getItemCountFormatted() {
        const length = this.getItemCount() || 0;
        return length > MAX_CART_COUNT ? MAX_CART_COUNT : length;
    }

    getItemBeingUpdated() {
        return this._updatingItem;
    }

    /**
     * Returns a boolean if the user is a trade user
     * @returns {boolean}
     */
    isTradeUser() {
        return (
            User.loggedIn() && User.canRequestNetPrice() && enabler.isEnabled('enable_net_price')
        );
    }

    /**
     * Returns true if the item is in the cart.
     * @param item {Backbone.Model} The item to check.
     * @returns {boolean}
     */
    isItemInCart(item) {
        return this._cartItems.findWhere({ 'item.id': item.get('id') }) ? true : false;
    }

    /**
     * Handler for errors when performing cart actions
     * @param payload
     * @param payload.message {String} The error message
     */
    onCartError(payload) {
        const { message } = payload;
        this._errorMessage = message;
        this._cartItems.trigger('cart:error', this._errorMessage);
        this.emitChange();
    }

    onCartPortfolioLoaded(payload) {
        const { itemCount } = payload;
        this._errorMessage = null;
        this._portfolioModel.set('itemCount', itemCount);
        this._cartItems.trigger('cart:loaded');
        this.emitChange();
    }

    onItemAdded(payload) {
        const { item } = payload;
        this._cartItems.add(item);
        this._updateItemCount();

        this._cartItems.trigger('cart:itemAdded', { item: item });
        this.emitChange();
    }

    onItemRemoved(payload) {
        const { item } = payload;
        this._cartItems.remove(item);
        this._updateItemCount();

        this._cartItems.trigger('cart:itemRemoved', { item: item });
        this.emitChange();
    }

    onItemRemovedAtIndex(payload) {
        const { index } = payload;
        const item = this._cartItems.at(index);
        this.onItemRemoved({ item });
    }

    onCurrencyChanged(payload) {
        const { currency } = payload;
        this._currency = currency.toLowerCase();
        this.emitChange();
    }

    onItemUpdating(payload) {
        const { item } = payload;
        this._updatingItem = item;
        this.emitChange();
    }

    onItemUpdated() {
        this._updatingItem = null;
        this.emitChange();
    }

    onItemUpdateFailed(payload) {
        this.onCartError({ message: payload.err });
    }

    onShowNotification(payload) {
        const { items } = payload;
        this._isDisplayingNotification = true;
        this._recentItems = items;
        this.emitChange();
    }

    onHideNotification() {
        this._isDisplayingNotification = false;
        this._recentItems = [];
        this.emitChange();
    }

    /**
     * Currently, the only function of this method is to trigger a change notification when the
     * backbone model is updated, so no payload is required.
     */
    _onCartUpdated() {
        this._updateItemCount();
        const itemCount = this.getItemCount();
        chatUtils.customAttribute.itemsInCart(itemCount);

        this.emitChange();
    }

    _updateItemCount() {
        const itemCount = this.getItemCount();
        this._portfolioModel.set('itemCount', itemCount);
    }
}

CartStore.storeName = 'CartStore';

CartStore.handlers = {
    'cart:itemAdded': 'onItemAdded',
    'cart:itemRemoved': 'onItemRemoved',
    'cart:itemRemoved:error': 'onCartError',
    'cart:portfolio:loaded': 'onCartPortfolioLoaded',
    'cart:portfolio:error': 'onCartError',
    'cart:notification:show': 'onShowNotification',
    'cart:notification:hide': 'onHideNotification',
    [currencyActionConstants.CURRENCY_CHANGED]: 'onCurrencyChanged',
    'item:update:start': 'onItemUpdating', // TODO: maybe these should be in a different store
    'item:update:done': 'onItemUpdated',
    'item:update:failed': 'onItemUpdateFailed',
};

module.exports = CartStore;



// WEBPACK FOOTER //
// ./assets/js/global/stores/CartStore.es.js